import type { CategoryHue } from '@recipes/contracts';
import { cn } from '@recipes/ui';
import { Check, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { HUES, HUE_SOLID, HUE_SURFACE } from './hue-classes';

/** A small coloured dot in front of a label; decorative – the label says what it is. */
export function HueDot({ hue, className }: { hue: CategoryHue | null; className?: string }) {
  return (
    <span
      aria-hidden
      data-hue={hue ?? 'none'}
      className={cn(
        'inline-block size-2.5 shrink-0 rounded-full',
        hue ? HUE_SOLID[hue] : 'bg-muted-foreground/40',
        className,
      )}
    />
  );
}

/**
 * Pick one of the ten hues, or "automatic" (derived from the name). No free colour picker – the
 * scale is checked for contrast in every theme. A radio group with the colour names as labels.
 */
export function HuePicker({
  value,
  automatic,
  onChange,
  label,
  disabled,
}: {
  value: number | null | undefined;
  /** The hue that applies without a choice – shown on the "automatic" option. */
  automatic: CategoryHue;
  onChange: (hue: CategoryHue | null) => void;
  label: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const options: Array<CategoryHue | null> = [null, ...HUES];
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="flex flex-wrap gap-1.5"
      data-testid="hue-picker"
    >
      {options.map((hue) => {
        const checked = (value ?? null) === hue;
        const name = hue ? t(`appearance.hues.${hue}`) : t('appearance.hues.automatic');
        return (
          <button
            key={hue ?? 'auto'}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={name}
            title={name}
            disabled={disabled}
            onClick={() => onChange(hue)}
            data-testid={`hue-option-${hue ?? 'auto'}`}
            className={cn(
              'flex size-9 items-center justify-center rounded-full border-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
              checked ? 'border-foreground' : 'border-transparent',
            )}
          >
            <span
              className={cn(
                'flex size-6 items-center justify-center rounded-full',
                // The solid hue tells the ten apart; "automatic" is the soft one, dashed.
                hue ? `${HUE_SOLID[hue]} text-background` : HUE_SURFACE[automatic],
                hue === null && 'border border-dashed border-current',
              )}
            >
              {checked ? (
                <Check className="size-3.5" aria-hidden />
              ) : hue === null ? (
                <Sparkles className="size-3.5" aria-hidden />
              ) : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
